// frontend/src/pages/Webhooks.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Icon,
  IconButton,
  Spinner,
  Table,
  Text,
  Title,
  Alert,
} from '@nimbus-ds/components';
import { PlusCircleIcon, EditIcon, TrashIcon } from '@nimbus-ds/icons';
import { webhooksApi, type Webhook, type CreateWebhookData, type UpdateWebhookData } from '../services/api';
import { getEventLabel } from '../constants/webhookEvents';
import { WebhookModal } from '../components/WebhookModal';
import { DeleteConfirmModal } from '../components/DeleteConfirmModal';

const Webhooks: React.FC = () => {
  const navigate = useNavigate();
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [deletingWebhook, setDeletingWebhook] = useState<Webhook | null>(null);

  const fetchWebhooks = useCallback(async () => {
    try {
      setError(null);
      const response = await webhooksApi.list();
      setWebhooks(response.data);
    } catch (err) {
      setError('Error al cargar los webhooks');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleCreate = () => {
    setEditingWebhook(null);
    setIsModalOpen(true);
  };

  const handleEdit = (webhook: Webhook) => {
    setEditingWebhook(webhook);
    setIsModalOpen(true);
  };

  const handleDelete = (webhook: Webhook) => {
    setDeletingWebhook(webhook);
  };

  const handleSave = async (data: CreateWebhookData | UpdateWebhookData) => {
    setError(null);
    setSuccess(null);

    if (editingWebhook) {
      await webhooksApi.update(editingWebhook.id, data as UpdateWebhookData);
      setSuccess('Webhook actualizado correctamente');
    } else {
      await webhooksApi.create(data as CreateWebhookData);
      setSuccess('Webhook creado correctamente');
    }

    await fetchWebhooks();
  };

  const handleConfirmDelete = async () => {
    if (!deletingWebhook) return;

    setError(null);
    setSuccess(null);

    try {
      await webhooksApi.delete(deletingWebhook.id);
      setSuccess('Webhook eliminado correctamente');
      await fetchWebhooks();
    } catch (err) {
      setError('Error al eliminar el webhook');
      throw err;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box
        height="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <Box padding="4" display="flex" flexDirection="column" gap="4">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Title as="h1">Webhooks</Title>
        <Box display="flex" gap="2">
          <Button onClick={handleCreate}>
            <Icon source={<PlusCircleIcon />} />
            Nuevo Webhook
          </Button>
          <Button appearance="transparent" onClick={() => navigate('/settings')}>
            Configuracion
          </Button>
          <Button appearance="transparent" onClick={() => navigate('/')}>
            Ir a n8n
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert appearance="danger" title="Error">
          {error}
        </Alert>
      )}

      {success && (
        <Alert appearance="success" title="Exito">
          {success}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {webhooks.length === 0 ? (
            <Box padding="6" display="flex" flexDirection="column" alignItems="center" gap="2">
              <Text color="neutral-textDisabled">No hay webhooks configurados</Text>
              <Text fontSize="caption" color="neutral-textDisabled">
                Crea un webhook para recibir notificaciones de eventos en n8n
              </Text>
            </Box>
          ) : (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Cell as="th">Evento</Table.Cell>
                  <Table.Cell as="th">URL</Table.Cell>
                  <Table.Cell as="th">Creado</Table.Cell>
                  <Table.Cell as="th" width="100px">Acciones</Table.Cell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {webhooks.map((webhook) => (
                  <Table.Row key={webhook.id}>
                    <Table.Cell>
                      <Text fontWeight="medium">{getEventLabel(webhook.event)}</Text>
                      <Text fontSize="caption" color="neutral-textDisabled">
                        {webhook.event}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text
                        fontSize="caption"
                        style={{
                          wordBreak: 'break-all',
                          maxWidth: '300px',
                        }}
                      >
                        {webhook.url}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="caption">{formatDate(webhook.created_at)}</Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Box display="flex" gap="1">
                        <IconButton
                          source={<EditIcon />}
                          size="2rem"
                          onClick={() => handleEdit(webhook)}
                        />
                        <IconButton
                          source={<TrashIcon />}
                          size="2rem"
                          onClick={() => handleDelete(webhook)}
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Card.Body>
      </Card>

      <WebhookModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        webhook={editingWebhook}
      />

      <DeleteConfirmModal
        isOpen={!!deletingWebhook}
        onClose={() => setDeletingWebhook(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar Webhook"
        message={`¿Estás seguro de eliminar el webhook para "${deletingWebhook ? getEventLabel(deletingWebhook.event) : ''}"? Esta acción no se puede deshacer.`}
      />
    </Box>
  );
};

export default Webhooks;
